class VariantPicker extends HTMLElement {
  constructor() {
    super();
    const dataScript = this.querySelector('[data-product-json]');
    this.variants = dataScript ? JSON.parse(dataScript.textContent) : [];
    this.productUrl = this.dataset.productUrl;
    this.addEventListener('change', () => this.onOptionChange());
  }

  getSelectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
    return fieldsets
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  findMatchingVariant(selectedOptions) {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    );
  }

  onOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findMatchingVariant(selectedOptions);

    this.querySelectorAll('fieldset[data-option-position]').forEach((fieldset, index) => {
      fieldset.querySelectorAll('.variant-picker__value').forEach((label) => {
        const input = label.querySelector('input');
        label.classList.toggle('is-active', input?.value === selectedOptions[index]);
      });
    });

    this.querySelectorAll('[data-selected-value]').forEach((el, index) => {
      if (selectedOptions[index]) el.textContent = selectedOptions[index];
    });

    const form = document.getElementById(this.dataset.section ? `product-form-${this.dataset.section}` : '');
    const priceWrapper = document.querySelector(`[data-price-wrapper="${this.dataset.section}"]`);
    const buyButtons = document.querySelector(`[data-buy-buttons="${this.dataset.section}"]`);

    if (!variant) {
      if (buyButtons) {
        buyButtons.querySelectorAll('button').forEach((button) => {
          button.disabled = true;
          button.dataset.originalText ||= button.textContent;
          button.textContent = button.dataset.unavailableText || button.textContent;
        });
      }
      return;
    }

    document.dispatchEvent(
      new CustomEvent('sebella:variant-change', {
        detail: { variant },
      })
    );

    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;
    }

    if (this.productUrl) {
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    if (priceWrapper) {
      const regular = priceWrapper.querySelector('[data-price-regular]');
      const sale = priceWrapper.querySelector('[data-price-sale]');
      const compare = priceWrapper.querySelector('[data-price-compare]');
      if (variant.compare_at_price) {
        priceWrapper.classList.add('price--on-sale');
        if (sale) sale.textContent = formatMoney(variant.price_cents);
        if (sale) sale.hidden = false;
        if (compare) compare.textContent = formatMoney(variant.compare_at_price_cents);
        if (compare) compare.hidden = false;
        if (regular) regular.hidden = true;
      } else {
        priceWrapper.classList.remove('price--on-sale');
        if (regular) regular.textContent = formatMoney(variant.price_cents);
        if (regular) regular.hidden = false;
        if (sale) sale.hidden = true;
        if (compare) compare.hidden = true;
      }
    }

    if (buyButtons) {
      buyButtons.querySelectorAll('button[type="submit"]').forEach((button) => {
        button.disabled = !variant.available;
        button.dataset.originalText ||= button.textContent;
        button.textContent = variant.available
          ? button.dataset.originalText || button.textContent
          : button.dataset.unavailableText || button.textContent;
      });
    }

    const section = this.closest('section.product-hero');
    if (section && variant) {
      const skuValue = section.querySelector('[data-product-sku] .product-main__sku-value');
      if (skuValue) skuValue.textContent = variant.sku || skuValue.dataset.fallback || '';
      const availabilityEl = section.querySelector('[data-product-availability]');
      if (availabilityEl) updateAvailability(availabilityEl, variant);
    }
  }
}

function updateAvailability(el, variant) {
  const dot = el.querySelector('.product-main__availability-dot');
  const textEl = el.querySelector('.product-main__availability-text');
  const countEl = el.querySelector('[data-product-inventory-count]');
  const threshold = parseInt(el.dataset.lowThreshold, 10) || 5;

  el.classList.remove(
    'product-main__availability--in_stock',
    'product-main__availability--out_of_stock',
    'product-main__availability--low_stock'
  );

  let statusText = '';
  let dotColor = '';

  if (!variant.available) {
    el.classList.add('product-main__availability--out_of_stock');
    statusText = el.dataset.textOutOfStock || window.themeStrings.outOfStock;
    dotColor = '#999';
  } else if (variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
    el.classList.add('product-main__availability--low_stock');
    statusText = (el.dataset.textLowStock || window.themeStrings.lowStock).replace('__COUNT__', variant.inventory_quantity);
    dotColor = '#e67e22';
  } else {
    el.classList.add('product-main__availability--in_stock');
    statusText = el.dataset.textInStock || window.themeStrings.inStock;
    dotColor = '#27ae60';
  }

  if (textEl) textEl.textContent = statusText;
  if (dot) dot.style.backgroundColor = dotColor;

  if (countEl && variant.available && variant.inventory_quantity > 0) {
    countEl.textContent = (el.dataset.textCount || window.themeStrings.inStockCount).replace('__COUNT__', variant.inventory_quantity);
  } else if (countEl) {
    countEl.textContent = '';
  }
}

function formatMoney(cents) {
  try {
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'USD';
    const lang = document.documentElement.lang || 'en';
    return new Intl.NumberFormat(lang, { style: 'currency', currency }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

function calculateSaveBadgeText(variant, container) {
  const price = Number(variant && variant.price_cents);
  const compareAtPrice = Number(variant && variant.compare_at_price_cents);
  if (!Number.isFinite(price) || !Number.isFinite(compareAtPrice) || compareAtPrice <= price) return '';

  const discountAmount = compareAtPrice - price;
  const discountPercentage = Math.max(1, Math.round((discountAmount / compareAtPrice) * 100));
  const saveText = container.dataset.saveText || 'SAVE';
  const offText = container.dataset.offText || 'OFF';

  switch (container.dataset.saveFormat) {
    case 'text':
      return saveText;
    case 'amount':
      return `${saveText} ${formatMoney(discountAmount)}`;
    case 'percentage_off':
      return `${discountPercentage}% ${offText}`;
    case 'custom_prefix':
      return `${container.dataset.savePrefix || saveText} ${discountPercentage}%`;
    default:
      return `${saveText} ${discountPercentage}%`;
  }
}

function refreshProductBadgeVisibility(group) {
  const maxBadges = Number.parseInt(group.dataset.maxBadges, 10) || 3;
  const priority = (group.dataset.badgePriority || '').split(',');
  const badges = [...group.querySelectorAll('[data-product-badge]')].sort(
    (a, b) => priority.indexOf(a.dataset.productBadge) - priority.indexOf(b.dataset.productBadge),
  );
  let visibleBadges = 0;
  badges.forEach((badge) => {
    const active = badge.dataset.badgeActive !== 'false';
    if (active) visibleBadges += 1;
    badge.hidden = !active || visibleBadges > maxBadges;
  });
}

function updateSaveBadge(group, variant) {
  const badge = group.querySelector('[data-product-badge="save"]');
  if (!badge) return;
  const text = calculateSaveBadgeText(variant, group);
  badge.textContent = text;
  badge.dataset.badgeActive = text ? 'true' : 'false';
  refreshProductBadgeVisibility(group);
}

function updateVariantSaveBadges(variant) {
  document.querySelectorAll('[data-product-badge-group][data-variant-aware="true"]').forEach((group) => {
    updateSaveBadge(group, variant);
  });
}

document.addEventListener('sebella:variant-change', (event) => {
  updateVariantSaveBadges(event.detail && event.detail.variant);
});

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="number"]');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.input) return;
      const step = parseInt(this.input.step, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;
      const current = parseInt(this.input.value, 10) || min;
      if (button.dataset.action === 'increase') this.input.value = current + step;
      if (button.dataset.action === 'decrease') this.input.value = Math.max(min, current - step);
      if (this.input) {
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onResize = this.debounce(() => this.truncateTitle(), 150);
    this._onDocumentQuantityChange = this._onDocumentQuantityChange.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.variantInput = this.querySelector('[data-sticky-variant-input]');
    this.quantityInput = this.querySelector('[data-sticky-quantity]');
    this.variantTitle = this.querySelector('[data-sticky-variant-title]');
    this.price = this.querySelector('[data-sticky-price]');
    this.button = this.querySelector('button[name="add"]');
    this.titleEl = this.querySelector('[data-sticky-title]');
    this.fullTitle = this.titleEl ? (this.titleEl.dataset.stickyFullTitle || this.titleEl.textContent) : '';

    document.addEventListener('sebella:variant-change', this._onVariantChange);
    window.addEventListener('resize', this._onResize);
    document.addEventListener('change', this._onDocumentQuantityChange);

    this._syncQuantityFromMain();
    this.initVisibility();
    this.truncateTitle();
  }

  disconnectedCallback() {
    this._initialized = false;
    document.removeEventListener('sebella:variant-change', this._onVariantChange);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('change', this._onDocumentQuantityChange);
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _onVariantChange(event) {
    this.updateVariant(event.detail.variant);
  }

  _onDocumentQuantityChange(event) {
    const input = event.target;
    if (!input || input.name !== 'quantity') return;
    const form = input.closest('form[action*="/cart/add"]:not([data-sticky-form])');
    if (!form) return;
    this._mainQuantityInput = input;
    if (this.quantityInput) {
      this.quantityInput.value = input.value;
    }
  }

  _syncQuantityFromMain() {
    const mainForm = document.querySelector('form[action*="/cart/add"]:not([data-sticky-form])');
    if (!mainForm) return;
    const mainQuantity = mainForm.querySelector('input[name="quantity"]');
    if (mainQuantity && this.quantityInput) {
      this.quantityInput.value = mainQuantity.value;
    }
    this._mainQuantityInput = mainQuantity;
  }

  initVisibility() {
    const mainForm = document.querySelector('form[action*="/cart/add"]:not([data-sticky-form])');
    if (!mainForm) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -50px 0px' }
    );

    const buyButtons = mainForm.querySelector('[data-buy-buttons]') || mainForm.querySelector('.product-main__buy-buttons, .product-hero__buy-buttons, .product-form__buttons');
    this._observer.observe(buyButtons || mainForm);
  }

  updateVariant(variant) {
    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.variantTitle) this.variantTitle.textContent = variant.options.join(' / ');
    if (this.price) this.price.textContent = formatMoney(variant.price_cents);

    if (this.button) {
      this.button.disabled = !variant.available;
      this.button.textContent = variant.available
        ? this.button.dataset.originalText || this.button.textContent
        : this.button.dataset.unavailableText || this.button.textContent;
    }

    this._syncQuantityFromMain();
  }

  truncateTitle() {
    if (!this.titleEl || !this.fullTitle) return;
    const limit = 15;
    if (window.innerWidth < 750 && this.fullTitle.length > limit) {
      this.titleEl.textContent = this.fullTitle.slice(0, limit) + '...';
    } else {
      this.titleEl.textContent = this.fullTitle;
    }
  }

  debounce(fn, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

// Intercept Add to Cart submissions for a smooth, in-place experience.
document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  let submitter = event.submitter;
  if (!submitter) {
    submitter = form.querySelector('button[type="submit"][name="add"]');
  }
  if (!form || !submitter || submitter.name !== 'add') return;

  event.preventDefault();

  const button = submitter;
  const originalText = button.textContent.trim();
  const addedText = button.dataset.addedText || originalText;
  button.disabled = true;
  button.classList.add('is-loading');

  const formData = new FormData(form);
  const item = {
    id: formData.get('id'),
    quantity: parseInt(formData.get('quantity') || '1', 10),
    properties: {},
  };

  for (const [key, value] of formData.entries()) {
    if (key.startsWith('properties[')) {
      const match = key.match(/^properties\[(.+)]$/);
      if (match) item.properties[match[1]] = value;
    }
  }

  const body = new URLSearchParams();
  body.set('items[0][id]', item.id);
  body.set('items[0][quantity]', item.quantity);
  Object.entries(item.properties || {}).forEach(([key, value]) => {
    body.set(`items[0][properties][${key}]`, value);
  });

  fetch('/cart/add.js', {
    method: 'POST',
    body,
  })
    .then((response) => response.json())
    .then(() => {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.classList.remove('is-pulse');
        void el.offsetWidth;
        el.classList.add('is-pulse');
        setTimeout(() => el.classList.remove('is-pulse'), 500);
      });

      button.classList.remove('is-loading');
      button.classList.add('is-success');
      button.textContent = addedText;

      const drawer = document.querySelector('cart-drawer');
      const updateDrawer = () => {
        if (drawer && typeof drawer.open === 'function') drawer.open(true);
        if (drawer && typeof drawer.refresh === 'function') {
          drawer.refresh();
          fetch('/cart.js')
            .then((response) => response.json())
            .then((cart) => {
              document.querySelectorAll('[data-cart-count]').forEach((el) => {
                el.textContent = cart.item_count;
                el.hidden = cart.item_count === 0;
              });
            });
        }
      };
      if (drawer && typeof drawer.open === 'function') {
        updateDrawer();
      } else if (drawer) {
        customElements.whenDefined('cart-drawer').then(updateDrawer);
      }

      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-success');
        button.textContent = originalText;
      }, 2000);
    })
    .catch(() => {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalText;
    });
});
